import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gavel } from "lucide-react";
import { api } from "@/lib/api";

const pad = (n) => String(n).padStart(2, "0");

// Countdown is driven by the server clock: we store the offset between server_time and the
// browser clock and never trust the device time as the source of truth.
export const Countdown = ({ endDate, offsetMs = 0, testid }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = new Date(endDate).getTime() - (now + offsetMs);
  if (!endDate) return null;
  if (diff <= 0)
    return (
      <span data-testid={testid} className="font-mono text-sm font-bold text-muted-foreground">
        Süre doldu
      </span>
    );
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  return (
    <span data-testid={testid} className="font-mono text-sm font-bold text-destructive">
      {d > 0 ? `${d} gün ` : ""}
      {pad(Math.floor((s % 86400) / 3600))}:{pad(Math.floor((s % 3600) / 60))}:{pad(s % 60)}
    </span>
  );
};

export default function AuctionShowcase() {
  const [items, setItems] = useState(null);
  const offset = useRef(0);

  useEffect(() => {
    api
      .get("/auctions", { params: { page_size: 6 } })
      .then((r) => {
        if (r.data.server_time) offset.current = Date.now() - new Date(r.data.server_time).getTime();
        const live = r.data.items.filter((a) => a.status === "LIVE");
        const soon = r.data.items.filter((a) => a.status === "SCHEDULED");
        setItems([...live, ...soon].slice(0, 3));
      })
      .catch(() => setItems([]));
  }, []);

  if (!items?.length) return null;

  return (
    <section className="border-y border-border bg-primary text-primary-foreground" data-testid="home-auction-showcase">
      <div className="mx-auto max-w-7xl px-4 py-14 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground">
              <Gavel className="h-4 w-4" /> Ciftora Mezat
            </p>
            <h2 className="mt-4 font-display text-2xl font-extrabold sm:text-3xl">Canlı mezatlar</h2>
            <p className="mt-2 text-sm opacity-85 md:text-base">
              Geri sayım sunucu saatiyle çalışır. Son dakika teklifleri süreyi otomatik uzatır.
            </p>
          </div>
          <Link
            to="/mezat"
            data-testid="showcase-all-auctions"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-accent hover:underline"
          >
            Tüm mezatlar <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {items.map((a) => (
            <Link
              key={a.id}
              to={`/mezat/${a.id}`}
              data-testid={`showcase-auction-${a.id}`}
              className="rounded-xl border border-white/20 bg-white/5 p-5 transition-colors duration-150 hover:border-accent"
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-bold ${
                    a.status === "LIVE" ? "bg-destructive text-white" : "bg-accent text-accent-foreground"
                  }`}
                >
                  {a.status === "LIVE" ? "Canlı" : "Başlayacak"}
                </span>
                <Countdown
                  endDate={a.status === "LIVE" ? a.end_date : a.start_date}
                  offsetMs={offset.current}
                  testid={`showcase-countdown-${a.id}`}
                />
              </div>
              <h3 className="mt-4 font-display text-lg font-extrabold">{a.title}</h3>
              <p className="mt-2 text-sm opacity-80">
                {a.lot_count} lot · {a.bid_count} teklif{a.province ? ` · ${a.province}` : ""}
              </p>
              <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-accent">
                Mezata katıl <ArrowRight className="h-4 w-4" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
